import { __extends, __assign } from 'tslib';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { classNames } from 'uitk-react-utils';
import styles from './uitk-dialog.css';
import { UitkPrimaryButton, UitkTertiaryButton } from 'uitk-react-button';

var getClassNameList$3 = classNames.bind(styles);
var UitkDialogAnimationType;
(function (UitkDialogAnimationType) {
    UitkDialogAnimationType["NONE"] = "none";
    UitkDialogAnimationType["FADE"] = "fade";
    UitkDialogAnimationType["SLIDEDOWN"] = "slidedown";
    UitkDialogAnimationType["SLIDELEFT"] = "slideleft";
    UitkDialogAnimationType["SLIDERIGHT"] = "slideright";
    UitkDialogAnimationType["SLIDEUP"] = "slideup";
})(UitkDialogAnimationType || (UitkDialogAnimationType = {}));
var UitkDialog = /** @class */ (function (_super) {
    __extends(UitkDialog, _super);
    function UitkDialog(props) {
        var _this = _super.call(this, props) || this;
        _this.focusableExist = false;
        _this.TIME = 200;
        _this.foundRef = false;
        _this.focusableElements = [];
        _this.containerElements = [];
        _this.iphoneSafariUARegex = /iphone.*safari|safari.*iphone/gi;
        _this.dialogRef = React.createRef();
        _this.setFocus = _this.setFocus.bind(_this);
        _this.trapFocusCallback = _this.trapFocusCallback.bind(_this);
        _this.setDialogHeightForSafariOnIphone = _this.setDialogHeightForSafariOnIphone.bind(_this);
        _this.setHeight = _this.setHeight.bind(_this);
        _this.detectIphoneSafariPortrait = _this.detectIphoneSafariPortrait.bind(_this);
        _this.focusableElements = ['BUTTON', 'A', 'IFRAME', 'INPUT', 'SELECT', 'TEXTAREA'];
        _this.containerElements = ['DIV', 'HEADER', 'FOOTER', 'ARTICLE', 'SECTION', 'FORM', 'NAV', 'ASIDE', 'MAIN', 'SPAN'];
        return _this;
    }
    UitkDialog.prototype.componentDidMount = function () {
        var returnFocusOnClose = this.props.returnFocusOnClose;
        if (returnFocusOnClose) {
            this.lastFocusedElement = document.activeElement;
        }
        this.setFocus();
        this.detectIphoneSafariPortrait() && this.setDialogHeightForSafariOnIphone();
    };
    UitkDialog.prototype.componentWillUnmount = function () {
        var returnFocusOnClose = this.props.returnFocusOnClose;
        if (returnFocusOnClose && this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
            this.lastFocusedElement.focus();
        }
        window.removeEventListener('resize', this.setHeight);
    };
    UitkDialog.prototype.setFocus = function () {
        var _this = this;
        var autoFocus = this.props.autoFocus;
        var dr = this.dialogRef;
        if (dr && dr.current && autoFocus) {
            var allChildren = Array.from(dr.current.getElementsByTagName('*'));
            var focusableChildren = allChildren.filter(function (ele) { return !_this.containerElements.includes(ele.tagName); });
            var firstFocusableChild = focusableChildren[0];
            if (firstFocusableChild) {
                !this.focusableElements.includes(firstFocusableChild.tagName) &&
                    firstFocusableChild.setAttribute('tabindex', '-1');
                firstFocusableChild.focus();
            }
        }
    };
    UitkDialog.prototype.setFocusableElems = function () {
        var dr = this.dialogRef;
        if (dr && dr.current && !this.focusableExist) {
            var focusable = dr.current.querySelectorAll('button, [href], iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])');
            this.firstFocusable = focusable[0];
            this.lastFocusable = focusable[focusable.length - 1];
            this.focusableExist = true;
        }
    };
    UitkDialog.prototype.setHeight = function () {
        document.documentElement.style.setProperty('--vh', window.innerHeight + "px");
    };
    UitkDialog.prototype.setDialogHeightForSafariOnIphone = function () {
        this.setHeight();
        window.addEventListener('resize', this.setHeight);
    };
    UitkDialog.prototype.detectIphoneSafariPortrait = function () {
        return (typeof window !== 'undefined' &&
            typeof navigator !== 'undefined' &&
            !!navigator.userAgent.match(this.iphoneSafariUARegex) &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(orientation: portrait)').matches);
    };
    UitkDialog.prototype.trapFocusCallback = function () {
        this.setFocusableElems();
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        document.activeElement &&
            document.activeElement.nextElementSibling &&
            document.activeElement.nextElementSibling.classList.contains('uitk-dialog')
            ? this.lastFocusable.focus()
            : this.firstFocusable.focus();
    };
    UitkDialog.prototype.render = function () {
        var _a = this.props, ariaLabel = _a.ariaLabel, children = _a.children, className = _a.className, closeCallback = _a.closeCallback, dialogShow = _a.dialogShow, openCallback = _a.openCallback, strictFullscreen = _a.strictFullscreen, heightAuto = _a.heightAuto;
        // Make focusableExist false on re-renders to cover the edge case of new dom node
        // insertion as it may change first or last focusable element order.
        this.focusableExist = false;
        if (!children) {
            return null;
        }
        var wrapperClassNames = getClassNameList$3('uitk-dialog', className, {
            'uitk-dialog-fullscreen-strict': strictFullscreen,
            'uitk-dialog-height-auto': heightAuto
        });
        return (React.createElement(CSSTransition, { classNames: {
                appear: getClassNameList$3("dialog-appear"),
                appearActive: getClassNameList$3("dialog-appear-active"),
                enter: getClassNameList$3("dialog-enter"),
                enterActive: getClassNameList$3("dialog-enter-active"),
                exit: getClassNameList$3("dialog-exit"),
                exitActive: getClassNameList$3("dialog-exit-active")
            }, delay: "0", duration: "500", in: dialogShow, appear: true, mountOnEnter: false, onEntered: openCallback, onExited: closeCallback, timingfn: "cubic-bezier", timeout: {
                enter: 300,
                exit: 500
            } },
            React.createElement(React.Fragment, null,
                React.createElement("div", { tabIndex: 0, onFocus: this.trapFocusCallback }),
                React.createElement("div", { className: wrapperClassNames, role: "dialog", "aria-modal": true, "aria-label": ariaLabel },
                    React.createElement("div", { className: getClassNameList$3('uitk-dialog-content-wrapper'), ref: this.dialogRef }, children)),
                React.createElement("div", { tabIndex: 0, onFocus: this.trapFocusCallback }))));
    };
    UitkDialog.displayName = 'UitkDialog';
    UitkDialog.defaultProps = {
        animation: UitkDialogAnimationType.NONE,
        autoFocus: true,
        heightAuto: false,
        returnFocusOnClose: false,
        strictFullscreen: false
    };
    return UitkDialog;
}(React.Component));

var getClassNameList$2 = classNames.bind(styles);
var FullscreenConditionalWrapper = function (_a) {
    var isFullscreen = _a.isFullscreen, children = _a.children;
    return isFullscreen ? React.createElement("div", { className: "uitk-dialog-content-container" }, children) : children;
};
var UitkDialogContent = /** @class */ (function (_super) {
    __extends(UitkDialogContent, _super);
    function UitkDialogContent() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    UitkDialogContent.prototype.render = function () {
        var _a = this.props, children = _a.children, className = _a.className, contentDomRef = _a.contentDomRef, zeroPadding = _a.zeroPadding, strictFullscreen = _a.strictFullscreen;
        var wrapperClassNames = getClassNameList$2('uitk-dialog-content', className, {
            'default-padding': !zeroPadding
        });
        return (React.createElement(FullscreenConditionalWrapper, { isFullscreen: strictFullscreen },
            React.createElement("div", { ref: contentDomRef, className: wrapperClassNames }, children)));
    };
    UitkDialogContent.displayName = 'UitkDialogContent';
    return UitkDialogContent;
}(React.PureComponent));

var getClassNameList$1 = classNames.bind(styles);
var UitkDialogFooter = /** @class */ (function (_super) {
    __extends(UitkDialogFooter, _super);
    function UitkDialogFooter() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    UitkDialogFooter.prototype.validDialogFooterButtonTypes = function (element) {
        return element.type.displayName === 'UitkPrimaryButton' || element.type.displayName === 'UitkTertiaryButton';
    };
    UitkDialogFooter.prototype.applyFooterButtonClasses = function (element) {
        return React.cloneElement(element, __assign(__assign({}, element.props), { className: getClassNameList$1(element.props.className, UitkDialogFooter.footerButtonWrapperClassNames) }));
    };
    UitkDialogFooter.prototype.render = function () {
        var _a = this.props, buttonOne = _a.buttonOne, buttonTwo = _a.buttonTwo, buttonThree = _a.buttonThree, stacked = _a.stacked, disabled = _a.disabled, children = _a.children;
        var Tag1 = buttonOne && buttonOne.type === 'primary' ? UitkPrimaryButton : UitkTertiaryButton;
        var Tag2 = buttonTwo && buttonTwo.type === 'primary' ? UitkPrimaryButton : UitkTertiaryButton;
        var Tag3 = buttonThree && buttonThree.type === 'primary' ? UitkPrimaryButton : UitkTertiaryButton;
        var footerWrapperClassName = getClassNameList$1('uitk-dialog-footer', {
            'uitk-dialog-footer-stacked': stacked
        }, {
            'is-disabled': disabled
        });
        var footerSectionWrapperClassNames = getClassNameList$1('uitk-dialog-footer-section');
        var childrenToRender = children &&
            React.Children.toArray(children)
                .filter(this.validDialogFooterButtonTypes)
                .slice(0, stacked ? 3 : 2)
                .map(this.applyFooterButtonClasses);
        if (childrenToRender) {
            return (React.createElement("div", { className: footerWrapperClassName }, childrenToRender.map(function (child, index) { return (React.createElement("div", { className: footerSectionWrapperClassNames, key: "footer-button-" + index }, child)); })));
        }
        return (React.createElement("div", { className: footerWrapperClassName },
            buttonOne && (React.createElement("div", { className: footerSectionWrapperClassNames },
                React.createElement(Tag1, { disabled: buttonOne.disabled || disabled, onClick: disabled ? undefined : buttonOne.onClick, className: getClassNameList$1(UitkDialogFooter.footerButtonWrapperClassNames), "data-stid": buttonOne.stid }, buttonOne.text))),
            buttonTwo && (React.createElement("div", { className: footerSectionWrapperClassNames },
                React.createElement(Tag2, { disabled: buttonTwo.disabled || disabled, onClick: disabled ? undefined : buttonTwo.onClick, className: getClassNameList$1(UitkDialogFooter.footerButtonWrapperClassNames), "data-stid": buttonTwo.stid }, buttonTwo.text))),
            stacked && buttonThree && (React.createElement("div", { className: footerSectionWrapperClassNames },
                React.createElement(Tag3, { disabled: buttonThree.disabled || disabled, onClick: disabled ? undefined : buttonThree.onClick, className: getClassNameList$1(UitkDialogFooter.footerButtonWrapperClassNames), "data-stid": buttonThree.stid }, buttonThree.text)))));
    };
    UitkDialogFooter.displayName = 'UitkDialogFooter';
    UitkDialogFooter.defaultProps = {
        stacked: false
    };
    UitkDialogFooter.footerButtonWrapperClassNames = 'uitk-dialog-footer-button';
    return UitkDialogFooter;
}(React.Component));

var getClassNameList = classNames.bind(styles);
var UitkFullscreenDialogTheme;
(function (UitkFullscreenDialogTheme) {
    UitkFullscreenDialogTheme["CANVAS"] = "uitk-dialog-fullscreen-bg-canvas";
    UitkFullscreenDialogTheme["DARK"] = "uitk-dialog-fullscreen-bg-dark";
    UitkFullscreenDialogTheme["DEFAULT"] = "uitk-dialog-fullscreen-bg-default";
})(UitkFullscreenDialogTheme || (UitkFullscreenDialogTheme = {}));
var UitkFullscreenDialog = /** @class */ (function (_super) {
    __extends(UitkFullscreenDialog, _super);
    function UitkFullscreenDialog() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    UitkFullscreenDialog.prototype.render = function () {
        var _a = this.props, className = _a.className, wideContent = _a.wideContent, theme = _a.theme;
        var wrapperClassNames = getClassNameList('uitk-dialog-fullscreen', theme, className, {
            'uitk-dialog-wide-content': wideContent
        });
        return React.createElement(UitkDialog, __assign({}, this.props, { className: wrapperClassNames }));
    };
    UitkFullscreenDialog.displayName = 'UitkFullscreenDialog';
    UitkFullscreenDialog.defaultProps = {
        theme: UitkFullscreenDialogTheme.DEFAULT,
        wideContent: false
    };
    return UitkFullscreenDialog;
}(React.Component));

export { UitkDialog, UitkDialogAnimationType, UitkDialogContent, UitkDialogFooter, UitkFullscreenDialog, UitkFullscreenDialogTheme };
